import React, { ChangeEvent, useState } from 'react'
import styles from 'styles/components/InputField.module.sass'

/**
 * Props for the InputField component.
 * 
 * @interface InputFieldProps
 * @property {string} label - The label text displayed for the input field.
 * @property {'email' | 'password' | 'text' | 'tel' | 'number'} type - The type of input field (e.g., email, password).
 * @property {string} value - The current value of the input field.
 * @property {(value: string) => void} onChange - Function to handle changes in the input field's value.
 * @property {string} [placeholder] - Optional placeholder text for the input field.
 * @property {boolean} required - Whether the input field is required.
 * @property {number} [rows] - Number of rows for textarea input type (default is 5).
 * @property {string} [labelColor] - Optional color for the label text (default is '#004264').
 */
interface InputFieldProps {
    label: string;
    type: 'email' | 'password' | 'text' | 'tel' | 'number'| 'textarea';
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    required: boolean;
    rows?: number;
    labelColor?: string;
    /** A problem with this field's value. Shown under the field and announced to screen readers. */
    error?: string | undefined;
}

/**
 * InputField component renders an input element with a label and supports various input types.
 * 
 * This component provides input tracking via the `onChange` function prop, displays a label, 
 * and includes optional placeholder text. It can also mark the input as required.
 * 
 * @component
 * @param {InputFieldProps} props - The props for the InputField component.
 * @param {string} props.label - The label text displayed for the input field.
 * @param {'email' | 'password' | 'text' | 'tel' | 'number'} props.type - The type of input field.
 * @param {string} props.value - The current value of the input field.
 * @param {(value: string) => void} props.onChange - Function to handle changes in the input value.
 * @param {string} [props.placeholder] - Optional placeholder text for the input field.
 * @param {boolean} props.required - Whether the input field is required.
 * 
 * @example
 * Example usage:
 * 
 * ```tsx
 * import InputField from 'components/common/InputField';
 * 
 * const [email, setEmail] = useState('');
 * 
 * return (
 *   <InputField
 *     label="Email"
 *     type="email"
 *     value={email}
 *     onChange={setEmail}
 *     placeholder="Enter your email"
 *     required={true}
 *   />
 * );
 * ```
 * 
 * @returns {JSX.Element} The JSX element for the input field.
 */
const InputField: React.FC<InputFieldProps> = ({ label, type, value, onChange, placeholder = '', required , rows = 5, labelColor = '#004264', error }) => {
    const [showPassword, setShowPassword] = useState(false)

    const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        onChange(e.target.value)
    }

    const resolvedType = type === 'password' && showPassword ? 'text' : type
    const errorId = error ? `${label.replace(/\s+/g, '-').toLowerCase()}-error` : undefined
    const invalid = error ? true : undefined

    return (
        <div className={styles['input-container']}>
            <label htmlFor={label} className={styles['label']} style={{ color: labelColor }}>
                {label}{required && (<span className={styles['required']}>*</span>)}
            </label>
            {type === 'textarea' ? (
                <textarea
                    aria-label={label}
                    value={value}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                    required={required}
                    className={styles['textarea-field']}
                    aria-invalid={invalid}
                    aria-describedby={errorId}
                    rows={rows}
                />
            ) : (
                <div className={styles['input-wrapper']}>
                    <input
                        aria-label={label}
                        type={resolvedType}
                        value={value}
                        onChange={handleInputChange}
                        placeholder={placeholder}
                        required={required}
                        className={error ? `${styles['input-field']} ${styles['input-field--invalid']}` : styles['input-field']}
                        aria-invalid={invalid}
                        aria-describedby={errorId}
                    />
                    {type === 'password' && (
                        <button
                            type="button"
                            className={styles['password-toggle']}
                            onClick={() => setShowPassword(v => !v)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                            {showPassword ? (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            )}
                        </button>
                    )}
                </div>
            )}
            {error && <span id={errorId} role="alert" className={styles['error-text']}>{error}</span>}
        </div>
    )
}

export default InputField;